import { forwardRef } from "react";
import { AlertTriangle, CheckCircle2, Info, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { CanvasBlock, CanvasData } from "../../api";
import Markdown from "../Markdown";
import CanvasChart from "./CanvasChart";

const formatCell = (value: unknown) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  // Plain numerals sent as text ("520000") get separators too; formatted text ("$1.2M", "11.9%") is kept.
  if (typeof value === "string" && /^-?\d{4,}(\.\d+)?$/.test(value.trim())) {
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
};

// Some models send table numbers as text ("1234.5", "11.9%", "$1,200"); align those like numbers too.
const isNumeric = (value: unknown) =>
  typeof value === "number" || (typeof value === "string" && /^[-+]?[$€£]?[\d,]*\.?\d+%?$/.test(value.trim()));

const CALLOUT = {
  info: { style: { background: "var(--blue-subtle)", borderColor: "var(--blue)", color: "var(--text-primary)" }, Icon: Info, iconColor: "var(--blue)" },
  success: { style: { background: "var(--success-card-subtle)", borderColor: "var(--success)", color: "var(--text-primary)" }, Icon: CheckCircle2, iconColor: "var(--success)" },
  warning: { style: { background: "var(--warning-subtle)", borderColor: "var(--warning)", color: "var(--text-primary)" }, Icon: AlertTriangle, iconColor: "var(--warning)" },
};

function Block({ block }: { block: CanvasBlock }) {
  switch (block.type) {
    case "heading":
      return <h2 className="text-lg font-bold text-primary-t">{block.text}</h2>;
    case "markdown":
      return <Markdown>{block.text}</Markdown>;
    case "metrics":
      return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
          {block.items.map((m, i) => {
            const TrendIcon = m.trend === "up" ? TrendingUp : m.trend === "down" ? TrendingDown : Minus;
            const trendColor = m.trend === "up" ? "var(--success)" : m.trend === "down" ? "var(--error)" : "var(--text-tertiary)";
            return (
              <div key={i} className="rounded-card border border-border-default-t bg-surface p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-tertiary-t">{m.label}</div>
                <div className="mt-1 break-words text-xl font-bold leading-tight text-primary-t">{m.value}</div>
                {m.delta && (
                  <div className="mt-1 flex items-center gap-1 text-xs font-semibold" style={{ color: trendColor }}>
                    {m.trend && <TrendIcon className="h-3.5 w-3.5" />}
                    {m.delta}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    case "table":
      return (
        <div>
          {block.title && <h3 className="mb-2 text-sm font-bold text-primary-t">{block.title}</h3>}
          <div className="max-h-[420px] overflow-auto rounded-card border border-border-default-t">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-surface">
                <tr>
                  {block.columns.map((c, j) => (
                    <th
                      key={c}
                      className={`border-b border-border-default-t px-3 py-2 font-semibold text-primary-t ${
                        block.rows.length > 0 && block.rows.every((row) => isNumeric(row[j])) ? "text-right" : "text-left"
                      }`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr key={i} className="bg-base">
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={`border-b border-border-subtle-t px-3 py-2 text-secondary-t ${isNumeric(cell) ? "text-right tabular-nums" : ""}`}
                      >
                        {formatCell(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    case "chart":
      return (
        <div className="rounded-card border border-border-default-t bg-base p-3">
          <CanvasChart block={block} />
        </div>
      );
    case "callout": {
      const tone = CALLOUT[block.tone ?? "info"] ?? CALLOUT.info;
      return (
        <div className="flex gap-3 rounded-card border p-4" style={tone.style}>
          <tone.Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: tone.iconColor }} />
          <Markdown className="min-w-0 flex-1">{block.text}</Markdown>
        </div>
      );
    }
    default:
      return null;
  }
}

/** Renders a canvas. The ref points at the printable area used for PNG/PDF export. */
const CanvasView = forwardRef<HTMLDivElement, { canvas: CanvasData }>(({ canvas }, ref) => (
  <div ref={ref} className="space-y-5 bg-base p-6">
    {canvas.blocks.map((block, i) => (
      <Block key={i} block={block} />
    ))}
  </div>
));

export default CanvasView;
